import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { PopupContainerComponent } from '../../components/popup-container/popup-container.component';
import { PopupPlacement } from '../../models/popup-placement.enum';
import { ComponentChanges } from '../../../models/component-changes.model';
import { fromEvent, Subscription } from 'rxjs';
import { DomService } from '../../../common/services/dom/dom.service';

type TriggerEvent = 'click' | 'mousemove';

interface ParsedTrigger {
  in: TriggerEvent;
  out: TriggerEvent;
}

@Directive({
  selector: '[ideomPopup]',
})
export class PopupDirective implements OnInit, OnChanges, OnDestroy {
  /**
   * The popup template
   */
  @Input() template: TemplateRef<any>;

  /**
   * The popup content
   */
  @Input() content: string;

  /**
   * The popup delay
   */
  @Input() delay = 0;

  /**
   * The popup content placement
   */
  @Input() placement: PopupPlacement = PopupPlacement.Bottom;

  /**
   * The triggers to show/hide popup
   */
  @Input() trigger = 'click:click';

  /**
   * The flag indicating that we should append popup to body
   */
  @Input() appendToBody = true; // TODO: Change default to false

  /**
   * The flag indicating is content visible
   */
  isVisible: boolean;

  /**
   * The flag indicating that showing is in progress
   * @private
   */
  private isShowing: boolean;

  /**
   * The flag indicating that hiding is in progress
   * @private
   */
  private isHiding: boolean;

  /**
   * The subscription for container outside click
   * @private
   */
  private closeSubscription: Subscription;

  /**
   * The delay timer id
   * @private
   */
  private delayTimeoutId: number;

  /**
   * The document events subscription
   * @private
   */
  private eventSubscription = new Subscription();

  /**
   * The component reference to container
   * @private
   */
  private component: ComponentRef<PopupContainerComponent>;

  /**
   * The parsed trigger events
   * @private
   */
  private get parsedTrigger(): ParsedTrigger {
    if (this.trigger) {
      const parts = this.trigger.split(':');
      if (parts.length === 2) {
        return {
          in: parts[0] as TriggerEvent,
          out: parts[1] as TriggerEvent,
        };
      }
      return null;
    }
    return null;
  }

  constructor(
    private el: ElementRef<any>,
    private viewContainerRef: ViewContainerRef,
    private factoryResolver: ComponentFactoryResolver,
    private domService: DomService,
    @Inject(DOCUMENT) private document
  ) {}

  /**
   * @ignore
   */
  ngOnInit(): void {
    const handleEvent = (event: MouseEvent, eventName: TriggerEvent) => {
      event.stopPropagation();
      const triggers = this.parsedTrigger;
      const isHostEvent = this.isMouseEventIsInElement(event, this.el.nativeElement);
      if (this.isVisible) {
        if (!this.isHiding && this.component) {
          const element = this.component.location.nativeElement;
          const isPopupEvent = this.isMouseEventIsInElement(event, element.firstElementChild);
          if (triggers.out === eventName && !isPopupEvent && !isHostEvent) {
            this.onHide();
          }
        }
      } else {
        if (!this.isShowing) {
          if (triggers.in === eventName && isHostEvent) {
            this.onShow();
          }
        }
      }
    };

    ['click', 'mousemove'].forEach((eventName: string) => {
      this.eventSubscription.add(
        fromEvent(this.document, eventName).subscribe((event: MouseEvent) => handleEvent(event, eventName as TriggerEvent))
      );
    });
  }

  ngOnChanges(changes: ComponentChanges<PopupDirective>): void {
    // not implemented
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    this.eventSubscription.unsubscribe();
  }

  /**
   * This method is used to show content
   * @private
   */
  private onShow(): void {
    if (!this.isVisible) {
      this.isShowing = true;
      const showPopup = () => {
        let component: ComponentRef<PopupContainerComponent>;
        if (this.appendToBody) {
          component = this.domService.appendToBody<PopupContainerComponent>(PopupContainerComponent);
        } else {
          const factory = this.factoryResolver.resolveComponentFactory(PopupContainerComponent);
          component = this.viewContainerRef.createComponent<PopupContainerComponent>(factory);
        }

        const element = this.el.nativeElement as HTMLElement;
        component.instance.rect = element.getBoundingClientRect();
        component.instance.template = this.template;
        component.instance.content = this.content;
        component.instance.placement = this.placement;
        component.instance.appendToBody = this.appendToBody;
        component.instance.scrollX = window.scrollX;
        component.instance.scrollY = window.scrollY;
        this.component = component;

        this.closeSubscription?.unsubscribe();
        this.closeSubscription = this.component.instance.onClose.subscribe(() => {
          if (this.isVisible) {
            this.onHide();
          }
        });
        this.isShowing = false;
        this.isVisible = true;
      };

      if (this.delayTimeoutId) {
        clearTimeout(this.delayTimeoutId);
        this.delayTimeoutId = 0;
      }

      this.delayTimeoutId = setTimeout(() => showPopup());
    }
  }

  /**
   * This method is used to hide content
   * @private
   */
  private onHide(): void {
    if (this.isVisible) {
      this.isHiding = true;
      this.isVisible = false;
      this.closeSubscription.unsubscribe();
      if (this.appendToBody) {
        this.domService.removeFromBody(this.component);
      } else {
        this.viewContainerRef.clear();
      }
      this.isHiding = false;
    }
  }

  /**
   * This method is used for checking event
   * @param event The mouse event
   * @param element The target element
   * @private
   */
  private isMouseEventIsInElement(event: MouseEvent, element: Element): boolean {
    const rect = element.getBoundingClientRect();
    return (
      event.clientX >= rect.left &&
      event.clientX <= rect.left + rect.width &&
      event.clientY >= rect.top &&
      event.clientY <= rect.top + rect.height
    );
  }
}
